import { type RequestHandler } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  type FieldNode,
  type IntrospectionObjectType,
  Kind,
  type SelectionSetNode,
} from 'graphql';

import { getSchema } from '@graphql/ApolloHasuraWrapper';

// Utility type to remove all object fields from a type except for the ones specified in TKeys.
export type NonObjectFields<T, TKeys extends string = never> = T extends Array<
  infer Item
>
  ? NonObjectFields<Item, TKeys>[]
  : T extends Record<string, any>
  ? {
      [K in keyof T as K extends Extract<TKeys, K>
        ? K
        : [T[K]] extends [Date | string | number | boolean | null | undefined]
        ? K
        : never]: NonObjectFields<
        T[K],
        TKeys extends `${Extract<K, string>}.${infer Rest}` ? Rest : never
      >;
    }
  : T;

const TYPE_MAP: Map<string, Map<string, IntrospectionObjectType>> = new Map();

// Helper function to find a field's type name
const getFieldTypeName = (
  parentTypeName: string,
  fieldName: string,
  typeMap: Map<string, IntrospectionObjectType>,
): string | null => {
  const type = typeMap.get(parentTypeName);
  if (!type) {
    return null;
  }

  const field = type.fields?.find(f => f.name === fieldName);
  if (!field) {
    return null;
  }

  // Unwrap non-null and list types
  let currentType = field.type;
  while ('ofType' in currentType && currentType.ofType) {
    currentType = currentType.ofType;
  }
  return 'name' in currentType ? currentType.name : null;
};

const addAvailableFieldsToSelectionSet = (
  selectionSet: SelectionSetNode | undefined,
  typeOrObject: string,
  parentType: string,
  typeMap: Map<string, IntrospectionObjectType>,
) => {
  if (!selectionSet) {
    return;
  }

  const parts = `${parentType}.${typeOrObject}`.split('.');
  let currentTypeName = 'query_root';

  for (const part of parts) {
    // If we're at the first part and it's not found in query_root,
    // try looking up the type directly in the type map
    if (currentTypeName === 'query_root') {
      // Check type map first since it's a direct lookup
      const type = typeMap.get(part);
      if (type) {
        currentTypeName = part;
        continue;
      }

      // Fall back to checking field type if not found in type map
      const nextTypeName = getFieldTypeName(currentTypeName, part, typeMap);
      if (!nextTypeName) {
        return false;
      }
      currentTypeName = nextTypeName;
    } else {
      const nextTypeName = getFieldTypeName(currentTypeName, part, typeMap);
      if (!nextTypeName) {
        return false;
      }
      currentTypeName = nextTypeName;
    }
  }

  const type = typeMap.get(currentTypeName);

  if (type?.kind === 'OBJECT') {
    selectionSet.selections = [
      ...type.fields
        .filter(
          f =>
            f.type.kind !== 'OBJECT' &&
            f.type.kind !== 'LIST' &&
            (f.type.kind !== 'NON_NULL' ||
              (f.type.ofType.kind !== 'OBJECT' &&
                f.type.ofType.kind !== 'LIST')),
        )
        .map(
          (f): FieldNode => ({
            kind: Kind.FIELD,
            name: {
              kind: Kind.NAME,
              value: f.name,
            },
          }),
        ),
      ...selectionSet.selections,
    ];
  }

  // Process nested selections
  for (const selection of selectionSet.selections) {
    if (selection.kind === 'Field' && selection.selectionSet) {
      addAvailableFieldsToSelectionSet(
        selection.selectionSet,
        selection.name.value,
        parentType ? `${parentType}.${typeOrObject}` : typeOrObject,
        typeMap,
      );
    }
  }
};

export const getAddAllSchemaFieldsLink = (
  typesToInclude: string[],
): RequestHandler => {
  const typeMapKey = [...typesToInclude].sort().join(',');

  return (operation, forward) => {
    const definition = getMainDefinition(operation.query);
    const schema = getSchema(operation);

    if (!schema) {
      return forward(operation);
    }

    let currentTypeMap = TYPE_MAP.get(typeMapKey);

    if (currentTypeMap == null) {
      currentTypeMap = new Map(
        schema.types
          .filter(t => t.kind === 'OBJECT' && typesToInclude.includes(t.name))
          .map(t => [t.name, t as IntrospectionObjectType]),
      );

      TYPE_MAP.set(typeMapKey, currentTypeMap);
    }

    if (definition.selectionSet?.selections) {
      for (const selection of definition.selectionSet.selections) {
        if (selection.kind === 'Field') {
          addAvailableFieldsToSelectionSet(
            selection.selectionSet,
            selection.name.value,
            'query_root',
            currentTypeMap,
          );
        }
      }
    }

    // ensure operation.query is updated by creating a new object
    operation.query = {
      ...operation.query,
      definitions: [...operation.query.definitions],
    };

    return forward(operation);
  };
};
